import Button from 'components/Button';
import FooterBasic from 'components/Footers/Basic';
import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputEmail from 'components/Form/InputEmail';
import InputPhoneNumber from 'components/Form/InputPhoneNumber';
import SubmitButton from 'components/Form/SubmitButton';
import HeaderBasic from 'components/Headers/Basic';
import DualPage from 'components/Layout/DualPage';
import Stacked from 'components/Layout/Stacked';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {t} from 'ttag';
import {FormFields} from 'types/utils';

import onboardingImage from '../assets/img/onboarding-1.png';
import {BusinessContactFormData} from '../types';
import WizardSectionHeader from '../WizardSectionHeader';

type BusinessContactProps = {
    onSuccess: (formData: BusinessContactFormData) => void;
    prefilledData?: Partial<BusinessContactFormData>;
};

const BusinessContact: FC<BusinessContactProps> = ({
    onSuccess,
    prefilledData,
}) => {
    const [useFax, setUseFax] = useState(!!prefilledData?.signupPhoneNumber);

    const methods = useForm<FormFields<BusinessContactFormData>>({
        defaultValues: {
            signupBusinessEmail: prefilledData?.signupBusinessEmail,
            signupFaxNumber: prefilledData?.signupFaxNumber,
            signupPhoneNumber: prefilledData?.signupPhoneNumber,
        },
        shouldUnregister: true,
    });
    const {handleSubmit} = methods;

    // disable the submit button after a successful submit
    const [success, setSuccess] = useState(false);

    const onSubmit = async (formData: BusinessContactFormData) => {
        setSuccess(true);
        onSuccess?.(formData);
    };

    return (
        <div
            className="relative flex min-h-screen flex-col bg-grey-100 "
            id="business-contact"
        >
            <HeaderBasic className="px-8" showSignIn={false} />
            <DualPage
                classNameFirstSection="bg-gradient-to-tr from-green-800 to-green-600"
                classNameInner="overflow-hidden rounded-lg"
                classNameSecondSection="bg-white"
                pagination={{
                    current: 1,
                    length: 3,
                }}
                size="6xl"
            >
                <>
                    <img
                        alt="post-signup-wizard-img"
                        className="invisible absolute bottom-0 w-full md:visible"
                        src={onboardingImage}
                    />
                    <WizardSectionHeader
                        subTitle={t`Let's get you set up to create invoices. Enter your business' contact information to the right.`}
                        title={t`Welcome to MakeLeaps!`}
                    />
                </>
                <FormProvider {...methods}>
                    <form className="p-6" onSubmit={handleSubmit(onSubmit)}>
                        <FormHeader
                            className="mt-0"
                            hideHR={true}
                            subtitle={t`To create your invoice, we need some additional information. You can change this anytime later.`}
                            title={t`Business Contact`}
                        />
                        <Stacked className="mt-4 w-full">
                            <div>
                                <InputPhoneNumber
                                    helpText={[
                                        t`e.g. 000-0000-0000`,
                                        t`Please enter your company's main business phone number.`,
                                    ].join('\n')}
                                    label={t`Company phone number`}
                                    name="signupPhoneNumber"
                                    required={true}
                                />
                            </div>
                            {!useFax && (
                                <Button
                                    className="border-transparent text-left text-sm text-green-700"
                                    kind="custom"
                                    onClick={() => setUseFax(true)}
                                >
                                    {t`Add a fax number`}
                                </Button>
                            )}
                            {useFax && (
                                <InputPhoneNumber
                                    helpText={t`e.g. 000-0000-0000`}
                                    label={t`Fax number`}
                                    name="signupFaxNumber"
                                />
                            )}
                            <InputEmail
                                helpText={t`You can use this email as the default Reply-To address for outgoing emails from MakeLeaps.`}
                                label={t`Business email`}
                                name="signupBusinessEmail"
                            />
                            <FormResult />
                            <FormActions>
                                <SubmitButton disabled={success}>
                                    {t`Next`}
                                </SubmitButton>
                            </FormActions>
                        </Stacked>
                    </form>
                </FormProvider>
            </DualPage>
            <FooterBasic />
        </div>
    );
};

export default BusinessContact;
